import {
  Badge,
  Button,
  Card,
  DropdownMenu,
  Flex,
  Separator,
  Text,
} from '@radix-ui/themes';

import {
  DotsHorizontalIcon,
  Pencil1Icon,
  TrashIcon,
} from '@radix-ui/react-icons';

import { FilterNode as BaseFilterNode } from '../../../../types';
import { NodeContextMenu } from '../../ContextMenu';
import { WorkflowNodeProps } from '..';
import NodeContainer from '../Container';
import CategorySection from '../CategorySection';
import { Handle, Position } from 'reactflow';
import ConditionContainer from '@/components/ConditionContainer';

export type FilterNode = BaseFilterNode & {
  filter: {
    name: string;
    conditions: Filter[];
  };
};

export enum FilterOperation {
  // Basic Comparisons
  Equals = 'EQUALS',
  NotEquals = 'NOT_EQUALS',
  GreaterThan = 'GREATER_THAN',
  GreaterThanOrEqual = 'GREATER_THAN_OR_EQUAL',
  LessThan = 'LESS_THAN',
  LessThanOrEqual = 'LESS_THAN_OR_EQUAL',
  Between = 'BETWEEN',
  NotBetween = 'NOT_BETWEEN',

  // String Operations
  Contains = 'CONTAINS',
  NotContains = 'NOT_CONTAINS',
  StartsWith = 'STARTS_WITH',
  EndsWith = 'ENDS_WITH',
  RegexMatch = 'REGEX_MATCH',

  // Existence / Null Checks
  IsNull = 'IS_NULL',
  IsNotNull = 'IS_NOT_NULL',
  Exists = 'EXISTS',
  NotExists = 'NOT_EXISTS',

  // List Membership
  InList = 'IN_LIST',
  NotInList = 'NOT_IN_LIST',

  // Logical Operators (for composite filters)
  And = 'AND',
  Or = 'OR',
  Not = 'NOT',

  // Default Routing
  Default = 'DEFAULT_ROUTE',

  // Advanced
  CustomExpression = 'CUSTOM_EXPRESSION',
}

enum FilterValueType {
  Literal = 'LITERAL',
  Variable = 'VARIABLE',
}

type FilterValue =
  | {
      type: FilterValueType.Variable;
      nodeId: string;
      outputKey: string;
    }
  | {
      type: FilterValueType.Literal;
      value: string | number | boolean | null;
    };

type Filter = {
  field: FilterValue;
  operation: FilterOperation;
  value: FilterValue;
};

interface FilterRuleProps {
  filter: Filter;
  handleDelete: () => void;
  handleEdit: (filter: Partial<Filter>) => void;
}

const operatorMap: Record<FilterOperation, string> = {
  [FilterOperation.Equals]: '=',
  [FilterOperation.NotEquals]: '!=',
  [FilterOperation.GreaterThan]: '>',
  [FilterOperation.GreaterThanOrEqual]: '≥',
  [FilterOperation.LessThan]: '<',
  [FilterOperation.LessThanOrEqual]: '≤',
  [FilterOperation.Between]: 'between',
  [FilterOperation.NotBetween]: 'not between',
  [FilterOperation.Contains]: 'contains',
  [FilterOperation.NotContains]: 'not contains',
  [FilterOperation.StartsWith]: 'starts with',
  [FilterOperation.EndsWith]: 'ends with',
  [FilterOperation.RegexMatch]: 'regex match',
  [FilterOperation.IsNull]: 'is null',
  [FilterOperation.IsNotNull]: 'is not null',
  [FilterOperation.Exists]: 'exists',
  [FilterOperation.NotExists]: 'not exists',
  [FilterOperation.InList]: 'in list',
  [FilterOperation.NotInList]: 'not in list',
  [FilterOperation.And]: 'and',
  [FilterOperation.Or]: 'or',
  [FilterOperation.Not]: 'not',
  [FilterOperation.Default]: 'default',
  [FilterOperation.CustomExpression]: '???',
};

const FilterDisplay = ({ operation, value }: Filter) => {
  if (!value) return 'Empty';

  return (
    <>
      <Text
        size="1"
        weight="medium"
        color="gray"
        style={{ fontFamily: 'monospace' }}
      >
        {operatorMap[operation]}
      </Text>
      {/* <ValueComponen value={value.value} /> */}
    </>
  );
};

interface FilterRuleProps {
  filter: Filter;
  handleDelete: () => void;
  handleEdit: (filter: Partial<Filter>) => void;
}

const FilterRuleExpandMenu = ({
  filter,
  handleDelete,
  handleEdit,
}: FilterRuleProps) => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger>
      <Button variant="ghost" size="1" m="1">
        <DotsHorizontalIcon width={12} height={12} />
      </Button>
    </DropdownMenu.Trigger>
    <DropdownMenu.Content sideOffset={5}>
      <DropdownMenu.Item onClick={() => handleEdit(filter)}>
        <Pencil1Icon width={16} height={16} />
        Edit filter
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={handleDelete} color="red">
        <TrashIcon width={16} height={16} />
        Delete filter
      </DropdownMenu.Item>
    </DropdownMenu.Content>
  </DropdownMenu.Root>
);

const FilterRule = ({ filter, handleDelete, handleEdit }: FilterRuleProps) => (
  <ConditionContainer>
    <Flex direction="row" gap="2" align="center">
      <Badge
        variant="surface"
        size="1"
        color="gray"
        style={{ fontFamily: 'monospace' }}
      >
        {filter.field.type === FilterValueType.Variable
          ? filter.field.outputKey
          : filter.field.value}
      </Badge>
      <FilterDisplay {...filter} />
    </Flex>
    <FilterRuleExpandMenu
      filter={filter}
      handleDelete={handleDelete}
      handleEdit={handleEdit}
    />
  </ConditionContainer>
);

const FilterNodeComponent = (props: WorkflowNodeProps<FilterNode>) => {
  const { id, data, onClick, onDelete } = props;
  console.log('FilterNode', data);

  return (
    <NodeContextMenu onDelete={() => onDelete(id)}>
      <NodeContainer title={data.filter.name} data={data}>
        <CategorySection
          title="Conditions"
          count={data.filter.conditions.length}
        >
          {data.filter.conditions.map((condition, index) => (
            <Card key={index} variant="ghost" size="1">
              <FilterRule
                key={index}
                filter={condition}
                handleDelete={() => onDelete(id)}
                handleEdit={() => {}}
              />
              {condition.field.type === FilterValueType.Variable && (
                <Handle
                  type="source"
                  position={Position.Left}
                  id={condition.field.outputKey}
                  style={{ background: 'var(--gray-9)' }}
                />
              )}
            </Card>
          ))}
        </CategorySection>
      </NodeContainer>
    </NodeContextMenu>
  );
};

export default FilterNodeComponent;
